// frontend/.../shared/SignaturePad.tsx
// Minimal canvas signature pad. Draws smoothed strokes with mouse or touch
// and exposes the result as a PNG data URL. Used by the public vendor
// re-qualification form to capture the vendor's acceptance signature.
import { useCallback, useEffect, useRef, useState } from 'react';
import Button from './Button';
import { TEXT } from './Typography';

interface Props {
  width?: number;
  height?: number;
  onChange?: (dataUrl: string | null) => void;
  disabled?: boolean;
}

export default function SignaturePad({
  width = 520,
  height = 140,
  onChange,
  disabled = false,
}: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const getCtx = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return null;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#111827';
    return ctx;
  }, []);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Hi-DPI support
    const dpr = window.devicePixelRatio || 1;
    c.width = width * dpr;
    c.height = height * dpr;
    c.style.width = `${width}px`;
    c.style.height = `${height}px`;
    const ctx = c.getContext('2d');
    ctx?.scale(dpr, dpr);
  }, [width, height]);

  function pos(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    drawing.current = true;
    const ctx = getCtx();
    if (!ctx) return;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    canvasRef.current?.setPointerCapture(e.pointerId);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || disabled) return;
    const ctx = getCtx();
    if (!ctx) return;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }
  function end(_e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    drawing.current = false;
    setHasInk(true);
    const c = canvasRef.current;
    if (c && onChange) onChange(c.toDataURL('image/png'));
  }

  function clear() {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    if (onChange) onChange(null);
  }

  return (
    <div className="inline-block">
      <div className="border border-gray-300 rounded bg-white">
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          className="touch-none select-none"
          style={{ cursor: disabled ? 'not-allowed' : 'crosshair' }}
        />
      </div>
      <div className={`flex items-center justify-between mt-1 ${TEXT.caption}`}>
        <span>{hasInk ? 'Signed' : 'Draw your signature above'}</span>
        <Button variant="link" onClick={clear} disabled={!hasInk || disabled}>
          Clear
        </Button>
      </div>
    </div>
  );
}
